import {
  BulbFilled,
  BulbOutlined,
  DatabaseFilled,
  EyeFilled,
  FileAddFilled,
  RobotFilled,
} from '@ant-design/icons'
import React from 'react'

import ChartsSvgIcon from 'components/icons/charts-svg-icon/ChartsSvgIcon'
import FeedbackSvgIcon from 'components/icons/feedback-svg-icon/FeedbackSvgIcon'
import KeywordsSvgIcon from 'components/icons/keywords-svg-icon/KeywordsSvgIcon'
import AlertsSvgIcon from 'features/project/partials/project-layout/components/navigation/components/alerts-svg-icon/AlertsSvgIcon'
import AskYogiSvgIcon from 'features/project/partials/project-layout/components/navigation/components/AskYogiSvgIcon'
import CatalogSvgIcon from 'features/project/partials/project-layout/components/navigation/components/catalog-svg-icon/CatalogSvgIcon'
import DashboardsSvgIcon from 'features/project/partials/project-layout/components/navigation/components/dashboards-svg-icon/DashboardsSvgIcon'
import PDPInsightsSvgIcon from 'features/project/partials/project-layout/components/navigation/components/pdp-insights-svg-icon/PdpInsightsSvgIcon'
import SavedSvgIcon from 'features/project/partials/project-layout/components/navigation/components/saved-svg-icon/SavedSvgIcon'
import { FeatureFlagsResponse } from 'features/project/service/feature-flags-service'
import type { ClientSettings } from 'types/project'

export type PageSettings = ClientSettings['project']['pages'] & {
  chadgent?: { alias?: string; hidden?: boolean }
}
import { ALERTS_URL } from 'features/alerts/paths'
import { ASK_YOGI_URL } from 'features/ask-yogi/paths'
import { CATALOG_URL } from 'features/catalog/paths'
import { CHARTS_2_URL } from 'features/charts-v2/paths'
import { COVERAGE_URL } from 'features/coverage/paths'
import { FEEDBACK_URL } from 'features/feedback/paths'
import { KEYWORDS_URL } from 'features/keywords/paths'
import { PDP_URL } from 'features/pdp/paths'
import { PRODUCT_WATCH_URL } from 'features/product-watch/paths'
import ExploreSvgIcon from 'features/project/partials/project-layout/components/navigation/components/ExploreSvgIcon/ExploreSvgIcon'
import { RECOMMENDATIONS_URL } from 'features/recommendations/paths'
import { SAVED_VIEWS_URL } from 'features/saved-views/paths'
import { SOURCE_UPLOAD_URL } from 'features/source-upload/paths'
import { AGENTS_URL, TREND_DETECTION_URL } from 'features/trend-detection/paths'
import { DASHBOARDS_2_1_URL } from 'src/features/dashboards-v2-1/paths'
import { DASHBOARDS_2_URL } from 'src/features/dashboards-v2/paths'

export type NavGroupId = 'explore'

export type NavigationItem = {
  title: string
  svgComponent: React.FC
  url: string
  enabled: boolean
  group?: NavGroupId
}

export const NAV_GROUPS: Record<
  NavGroupId,
  { title: string; svgComponent: React.FC }
> = {
  explore: { title: 'Explore', svgComponent: ExploreSvgIcon },
}

export type NavigationEntry =
  | { kind: 'item'; item: NavigationItem }
  | {
      kind: 'group'
      id: NavGroupId
      title: string
      svgComponent: React.FC
      children: NavigationItem[]
    }

// Coalesces consecutive same-group items into a single group entry, keeping
// the group's position wherever its first member sits in `items`. Consecutive
// (not bucketed-by-key) so the array literal in getNavigationItems() stays
// the single source of order — a group can't drift apart from its position.
export const groupNavigationItems = (
  items: NavigationItem[],
): NavigationEntry[] => {
  const entries: NavigationEntry[] = []
  for (const item of items) {
    if (!item.group) {
      entries.push({ kind: 'item', item })
      continue
    }
    const last = entries[entries.length - 1]
    if (last?.kind === 'group' && last.id === item.group) {
      last.children.push(item)
      continue
    }
    entries.push({
      kind: 'group',
      id: item.group,
      title: NAV_GROUPS[item.group].title,
      svgComponent: NAV_GROUPS[item.group].svgComponent,
      children: [item],
    })
  }
  return entries
}

type Props = {
  feature_flags: FeatureFlagsResponse | undefined
  pageSettings: PageSettings
  isYogiUser: boolean
  projectId: string
}

export const getNavigationItems = ({
  feature_flags,
  pageSettings,
  projectId,
}: Props): NavigationItem[] => [
  {
    title: pageSettings?.chadgent?.alias || 'Ask Yogi',
    svgComponent: AskYogiSvgIcon,
    url: ASK_YOGI_URL,
    // hide_ask_yogi: This is to hide Ask Yogi for Disney. Delete these checks once they approve Ask Yogi usage.
    enabled:
      !pageSettings.chadgent?.hidden && !feature_flags?.['hide_ask_yogi'],
  },
  {
    title: pageSettings?.custom_dashboards?.alias || 'Dashboards',
    svgComponent: DashboardsSvgIcon,
    url: DASHBOARDS_2_URL,
    enabled: true,
  },
  {
    title: 'Dashboards 2.1',
    svgComponent: DashboardsSvgIcon,
    url: DASHBOARDS_2_1_URL,
    enabled: !!feature_flags?.['dashboards_2_1'],
  },
  {
    title: pageSettings?.dashboard?.alias || 'Charts',
    svgComponent: ChartsSvgIcon,
    url: CHARTS_2_URL,
    enabled: true,
    group: 'explore',
  },
  {
    title: pageSettings?.feedback?.alias || 'Feedback',
    svgComponent: FeedbackSvgIcon,
    url: FEEDBACK_URL,
    enabled: !pageSettings.feedback?.hidden,
    group: 'explore',
  },
  {
    title: pageSettings?.catalog?.alias || 'Catalog',
    svgComponent: CatalogSvgIcon,
    url: CATALOG_URL,
    enabled: !pageSettings.catalog?.hidden,
    group: 'explore',
  },
  {
    title: pageSettings?.keywords?.alias || 'Keywords',
    svgComponent: KeywordsSvgIcon,
    url: KEYWORDS_URL,
    enabled: !pageSettings.keywords?.hidden,
    group: 'explore',
  },
  {
    title: 'Insights',
    svgComponent: BulbFilled,
    url: TREND_DETECTION_URL,
    enabled: !!feature_flags?.['trend_detection_prototype'],
  },
  {
    title: 'Agents',
    svgComponent: RobotFilled,
    url: AGENTS_URL,
    enabled: !!feature_flags?.['trend_detection_prototype'],
  },
  {
    title: 'Coverage',
    svgComponent: DatabaseFilled,
    url: COVERAGE_URL,
    enabled: !!feature_flags?.['ask_yogi_skill_social_topic_setup'],
  },
  {
    title: 'Source Upload',
    svgComponent: FileAddFilled,
    url: SOURCE_UPLOAD_URL,
    enabled: !!feature_flags?.['source_upload'],
  },
  {
    title: pageSettings?.product_watch?.alias || 'Product Watch',
    svgComponent: EyeFilled,
    url: PRODUCT_WATCH_URL,
    enabled:
      !!feature_flags?.['product_watch'] &&
      projectId === '74170bb4-7763-4ffb-8618-31b409b0173e',
  },
  {
    title: pageSettings?.pdp?.alias || 'PDP Insights',
    svgComponent: PDPInsightsSvgIcon,
    url: PDP_URL,
    enabled: !pageSettings.pdp?.hidden && !!feature_flags?.['pdp_tool'],
  },
  {
    title: pageSettings?.alerts?.alias || 'Alerts',
    svgComponent: AlertsSvgIcon,
    url: ALERTS_URL,
    enabled: !pageSettings.alerts?.hidden,
  },
  {
    title: pageSettings?.saved_views?.alias || 'Saved Views',
    svgComponent: SavedSvgIcon,
    url: SAVED_VIEWS_URL,
    enabled: !pageSettings.saved_views?.hidden,
  },
  {
    title: pageSettings?.recommendations?.alias || 'Recommendations',
    svgComponent: BulbOutlined,
    url: RECOMMENDATIONS_URL,
    enabled: !pageSettings.recommendations?.hidden,
  },
]
